'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { Crown, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import {
  createSubscriptionCheckoutSession,
  createCustomerPortalSession,
  getSubscriptionPlans,
  getCustomerSubscriptionDetails
} from '@/app/subscription/billing/server/stripe';
import { subscriptionPlanCredits, PlanCredits } from '@/app/subscription/plans';
import { safePathFromCallback } from '../utils/callbackSecurity';
import { SubscriptionPlan, CustomerSubscription } from '@/app/subscription/billing/server/data';
import { useTranslations } from 'next-intl';

export default function SubscriptionFrontend({ callbackBaseUrl = '/profile' }:
    { callbackBaseUrl?: string }) {
  const { userEmail } = useAuth();
  const t = useTranslations('subscription');
  const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [customerSubscriptions, setCustomerSubscriptions] = useState<CustomerSubscription[]>([]);
  const [subscriptionDetailsLoading, setSubscriptionDetailsLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(false);

  // Function to get plan descriptions based on plan name
  const getPlanDescriptions = (planName: string): string[] => {
    const planCredit = subscriptionPlanCredits.find(plan => 
      plan.name.toLowerCase() === planName.toLowerCase()
    );
    return planCredit?.description || ['Credits included', 'Priority processing', 'Advanced features', 'Priority support'];
  };

  // Function to get plan order based on plans.ts
  const getPlanOrder = (planName: string): number => {
    const planIndex = subscriptionPlanCredits.findIndex(plan => 
      plan.name.toLowerCase() === planName.toLowerCase()
    );
    return planIndex >= 0 ? planIndex : 999; // Put unmatched plans at the end
  };

  // Function to get plan icon based on plan name
  const getPlanIcon = (planName: string) => {
    const planNameLower = planName.toLowerCase();
    if (planNameLower.includes('basic')) {
      return Sparkles;
    } else if (planNameLower.includes('premium') || planNameLower.includes('pro')) {
      return Crown;
    }
    return Sparkles; // default
  };

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        const result = await getSubscriptionPlans();
        if (result.success && Array.isArray(result.plans)) {
          // Filter out plans with missing required data and transform the data
          const validPlans = result.plans
            .filter(plan => plan.name && plan.price !== null && plan.interval)
            .map(plan => ({
              ...plan,
              description: plan.description || null,
              price: plan.price || 0,
              interval: plan.interval || 'month'
            }));
          setSubscriptionPlans(validPlans);
        } else {
          console.error('Failed to fetch subscription plans:', result.error);
        }
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
      } finally {
        setPlansLoading(false);
      }
    };

    const fetchCustomerSubscriptionDetails = async () => {
      try {
        const result = await getCustomerSubscriptionDetails();
        if (result.success) {
          setCustomerSubscriptions(result.subscriptions || []);
        } else {
          console.error('Failed to fetch customer subscription details:', result.error);
        }
      } catch (error) {
        console.error('Error fetching customer subscription details:', error);
      } finally {
        setSubscriptionDetailsLoading(false);
      }
    };


    fetchSubscriptionPlans();
    fetchCustomerSubscriptionDetails();
  }, [userEmail]);


  const handleSubscribe = async (priceId: string) => {
    setSubscriptionLoading(priceId);
    try {
      const safeCallbackUrl = safePathFromCallback(callbackBaseUrl);
      const callbackQS = safeCallbackUrl ? `&callback=${encodeURIComponent(safeCallbackUrl)}` : '';
      const result = await createSubscriptionCheckoutSession({
        priceId,
        successUrl: `${process.env.NEXT_PUBLIC_APP_URL}/subscription/billing/success?session_id={CHECKOUT_SESSION_ID}${callbackQS}`,
        cancelUrl: `${process.env.NEXT_PUBLIC_APP_URL}/subscription/billing/cancel?session_id={CHECKOUT_SESSION_ID}${callbackQS}`,
      });

      if (!result.success) {
        console.error('Failed to create subscription checkout session:', result.error);
        alert('Failed to create subscription checkout session. Please try again.');
        return;
      }

      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSubscriptionLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const safeCallbackUrl = safePathFromCallback(callbackBaseUrl);
      const result = await createCustomerPortalSession(safeCallbackUrl || undefined);
      
      if (!result.success) {
        console.error('Failed to create portal session:', result.error);
        alert(`Failed to access subscription management: ${result.error}`);
        return;
      }

      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Error accessing subscription management:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  // Get active subscriptions
  const activeSubscriptions = customerSubscriptions.filter(subscription => subscription.is_active);

  return (
    <>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #2a2a2e;
          border-radius: 1px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #ffffff;
          border-radius: 1px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #e5e5e5;
        }
      `}</style>
      <div className="space-y-6">
        {/* Active Subscription move to userBalance.tsx */}

        {/* Subscription Plans & One-time Purchase */}
        <div className="bg-gradient-to-br from-[#1a1a1e] to-[#2a2a2e] rounded-2xl p-8 border border-white/5">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">{t('subscriptionPlans')}</h2>
          {plansLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-white" />
            </div>
          ) : subscriptionPlans.length > 0 ? ( 
            <div className="flex justify-center">
              <div className="flex gap-6 overflow-x-auto pb-4 max-w-full custom-scrollbar" style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#ffffff #2a2a2e'
              }}>
              {subscriptionPlans
                .sort((a, b) => getPlanOrder(a.name) - getPlanOrder(b.name))
                .map((plan, index) => {
                const isAlreadySubscribed = customerSubscriptions.some(
                  subscription => subscription.plan.price_id === plan.price_id && subscription.is_active
                );
                
                return (
                  <div 
                    key={plan.id} 
                    className={`bg-[#2a2a2e] rounded-xl p-4 border-2 transition-all flex-1 min-w-[260px] max-w-[320px] ${
                      isAlreadySubscribed 
                        ? 'border-gray-600' 
                        : 'border-[#7c3aed]'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2 mb-2">
                      {(() => {
                        const IconComponent = getPlanIcon(plan.name);
                        return <IconComponent className="w-5 h-5 text-white" />;
                      })()}
                      <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                    </div>
                    <div className="text-xl font-bold text-white mb-3 text-center">
                      ${((plan.price || 0) / 100).toFixed(2)}
                      <span className="text-xs text-gray-400 font-normal">/{plan.interval || 'month'}</span>
                    </div>
                    {plan.description && (
                      <p className="text-gray-300 mb-4 text-sm text-center">{plan.description}</p>
                    )}
                    <ul className="text-gray-300 space-y-2 mb-6 text-sm">
                      {getPlanDescriptions(plan.name).map((description, descIndex) => (
                        <li key={descIndex} className="flex items-center justify-start gap-2">
                          <div className="w-1 h-1 bg-[#4f46e5] rounded-full"></div>
                          {description}
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={() => !isAlreadySubscribed && handleSubscribe(plan.price_id)}
                      disabled={isAlreadySubscribed || subscriptionLoading === plan.price_id}
                      className={`w-full py-2 px-3 rounded-xl font-semibold transition-all text-sm ${
                        isAlreadySubscribed
                          ? 'bg-[#374151] text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] hover:from-[#5856d6] hover:to-[#8b5cf6] disabled:from-[#374151] disabled:to-[#374151] text-white disabled:cursor-not-allowed'
                      }`}
                    >
                      {isAlreadySubscribed
                        ? t('alreadySubscribed')
                        : subscriptionLoading === plan.price_id
                          ? t('processing')
                          : t('subscribeTo', { planName: plan.name })
                      }
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">{t('noPlansAvailable')}</p>
          </div>
        )}

        
        {/* Disclaimer */}
        <div className="mt-6 pt-6">
          <div className="flex items-start gap-3">
            <div className="text-[#f59e0b] text-lg mt-0.5">⚠️</div>
            <div className="flex-1">
              <p className="text-white font-semibold mb-1 text-sm uppercase tracking-wide">{t('importantNotice')}</p>
              <p className="text-gray-400 text-sm leading-relaxed">
                {t('chargesFinal')}
              </p>
            </div>
          </div>
        </div>

        {/* Support Section */}
         <div className="mt-6 pt-6 border-t border-white/10">
           <div className="flex items-start gap-3">
             <div className="text-[#10b981] text-lg mt-0.5">💬</div>
             <div className="flex-1">
               <p className="text-white font-semibold mb-1 text-sm uppercase tracking-wide">{t('support')}</p>
               <p className="text-gray-400 text-sm leading-relaxed">
                 {t('supportMessage')}{' '}
                 <a href="mailto:support@dangochat.com" className="text-[#6366f1] hover:text-[#5856d6] transition-colors">
                   support@dangochat.com
                 </a>
               </p>
               
               {/* Subscription ID Copy Section */}
               {activeSubscriptions.length > 0 && (
                 <div className="flex items-center gap-2 mt-3">
                   <p className="text-gray-400 text-sm">{t('copySubscriptionId')}</p>
                   <button 
                     onClick={() => {
                       navigator.clipboard.writeText(activeSubscriptions[0].id);
                       setCopiedId(true);
                       setTimeout(() => setCopiedId(false), 2000);
                     }}
                     className={`transition-colors ${copiedId ? 'text-gray-700' : 'text-gray-500 hover:text-white'}`}
                     title="Copy Subscription ID"
                   >
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                     </svg>
                   </button>
                 </div>
               )}
             </div>
           </div>
         </div>
        </div>
      </div>
    </>
  );
}